/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {checkCDN} from '../cdnConnect';
import {getQueryParams} from '@exc/url';
import {HTMLStatus} from '../enums';
import Metrics, {Level} from '@adobe/exc-app/metrics';
import {runInBackground, runWithTimeout} from '../utils';
import {Solution} from '../models/Solution';
import {SPAConfig, UnifiedShellConfig} from '../models/UnifiedShellConfig';
import {storage} from '@exc/storage';

/** Timeout for service worker to fetch and check HTML */
export const HTML_TIMEOUT = 20 * 1000;
export const ONE_HOUR = 60 * 60 * 1000;
export const ONE_DAY = 24 * ONE_HOUR;
const MANIFEST_CHECK_WAIT = 2 * 60 * 1000;
const MANIFEST_EP = '/service-worker/manifest';
const SW_CACHE_NAME = 'unifiedShellCache';

const TIMEOUT_RESPONSE = new Response(JSON.stringify({
  status: 'timeout'
}));

interface ReloadReasonData {
  appId?: string;
  newVersion: string;
  oldVersion: string;
}

export default class ServiceWorkerControllerService {
  private static SW_DISABLED = false;
  private readonly metrics = Metrics.create('exc.core.services.ServiceWorkerControllerService');
  private getWindowConfig = ():UnifiedShellConfig => (window as any).config as UnifiedShellConfig;

  /**
   * Checks if an SPA version has updated in the new config.
   * @param {string} appId - SPA app ID
   * @param {UnifiedShellConfig} config - Incoming config object
   * @param {UnifiedShellConfig} windowConfig - Current config object (window.config)
   * @returns true if version changed, false otherwise.
   */
  private async hasAppVersionChanged(
    appId: string,
    config: UnifiedShellConfig,
    windowConfig: UnifiedShellConfig
  ): Promise<ReloadReasonData|undefined> {
    const liveSolution: SPAConfig = windowConfig.solutions[appId];
    const newSolution: SPAConfig = config.solutions[appId];

    const changed = (!liveSolution && newSolution) ||
      (liveSolution && !newSolution) ||
      (liveSolution.liveVersion !== newSolution.liveVersion);

    if (changed) {
      return {
        appId,
        newVersion: newSolution?.liveVersion,
        oldVersion: liveSolution?.liveVersion
      };
    }
  }

  /**
   * Logs reload reason if previous load ended up in a reload to update HTML/SPA version.
   * @returns true if reload happened.
   * @private
   */
  private async isReload(): Promise<boolean> {
    const reasonDetails = await storage.session.get('reloadReason');
    if (reasonDetails) {
      const {reason, data} = reasonDetails;
      this.metrics.event(reason, data);
      storage.session.remove('reloadReason');
      return true;
    }
    return false;
  }

  /**
   * Sends a fetch request to ping service worker to check the live index.html
   * version, compare it against the cached index.html version, and update
   * cached assets as needed. We will also need to reload the page to update
   * the HTML in some cases.
   * @param {Promise<Response>} htmlPromise - Service Worker HTML check response
   * @param {Solution} solutionConfig Active solution config
   * @returns true if not reloading. false otherwise (But window.reload will cut execution)
   */
  public async checkHtmlForUpdates(htmlPromise?: Promise<Response>, solutionConfig?: Solution): Promise<HTMLStatus> {
    if (!htmlPromise) {
      return HTMLStatus.OK;
    }

    // If reloaded previously, log (See comment below).
    const isReloadPromise = this.isReload();

    const reload = async (event: string, data: ReloadReasonData, reason: string): Promise<HTMLStatus> => {
      // Never reload twice - That can be a potential loop.
      if (await isReloadPromise) {
        this.metrics.event('ServiceWorker.doubleReloadBlocked', data, {level: Level.ERROR});
        return HTMLStatus.OUTDATED;
      }

      this.metrics.event(event, data);
      await storage.session.set('reloadReason', {data, reason});
      window.location.reload();
      return HTMLStatus.OUTDATED;
    };

    try {
      const res = await runWithTimeout(htmlPromise, HTML_TIMEOUT, TIMEOUT_RESPONSE);
      const {data: statusData = {}, status} = await res.json();
      const {config, url} = statusData;

      if (status === 'outdated') {
        const windowConfig = this.getWindowConfig();

        // If spaVersion outdated
        const oldVersion = windowConfig.spaVersion;
        if (config.spaVersion !== oldVersion) {
          const versionData = {
            newVersion: config.spaVersion,
            oldVersion
          };
          return await reload('ServiceWorker.newUnifiedShellVersion', versionData, 'ReloadReason.newVersion');
        }

        // If solution that updated is current solution, refresh page.
        // Otherwise update outdated solution versions.
        const {spaAppId} = solutionConfig || {};
        const updateData = spaAppId && await this.hasAppVersionChanged(spaAppId, config, windowConfig);
        if (updateData) {
          return await reload('ServiceWorker.currentAppVersionUpdated', updateData, 'ReloadReason.newAppVersion');
        } else {
          this.metrics.event('ServiceWorker.appsVersionUpdated');
          // Copy solutions from new config.
          windowConfig.solutions = config.solutions;
          return HTMLStatus.OUTDATED;
        }
      } else if (status === 'timeout') {
        const ping = await checkCDN();
        this.metrics.event('ServiceWorker.timeout', {ping}, {level: Level.ERROR});
        return HTMLStatus.TIMEOUT;
      } else if (status === 'non standard url') {
        this.metrics.event('ServiceWorker.nonStandardUrl', {url});
        return HTMLStatus.OK;
      } else if (status === 'html structure changed') {
        this.metrics.event('ServiceWorker.badHtml', statusData, {level: Level.ERROR});
        return HTMLStatus.CORRUPT;
      } else if (status === 'failed to get html') {
        const ping = await checkCDN();
        this.metrics.event('ServiceWorker.unreachableHtml', Object.assign(statusData, {online: navigator.onLine, ping}), {level: Level.ERROR});
        return HTMLStatus.UNREACHABLE;
      } else {
        this.metrics.event('ServiceWorker.noHtmlUpdates');
      }
    } catch (err) {
      this.metrics.event('ServiceWorker.failedToCheckHTML', {error: err.message}, {level: Level.ERROR});
    }
    return HTMLStatus.OK;
  }

  private async checkDisabled(): Promise<void> {
    const disabled = await storage.local.get('shell_sw_disabled');
    if (disabled) {
      ServiceWorkerControllerService.SW_DISABLED = true;
    }
  }

  /**
   * Registers service worker.
   */
  public async registerServiceWorker(): Promise<void> {
    await this.checkDisabled();
    if (ServiceWorkerControllerService.SW_DISABLED) {
      this.metrics.event('App.serviceWorkerDisabled');
      return;
    }

    if (navigator.serviceWorker) {
      let sw = `${location.origin}/sw.js`;
      const version = getQueryParams()['unified-shell_version'];

      if (version) {
        sw += `?unified-shell_version=${version}`;
      }
      try {
        const registration = await navigator.serviceWorker.register(sw, {scope: '/'});
        if (registration.active) {
          this.metrics.event('ServiceWorker.active', {
            navigationPreload: !!registration.navigationPreload,
            state: registration.active.state
          });
        }
        if (registration.installing) {
          this.metrics.event('ServiceWorker.installing');
        }
        if (registration.waiting) {
          this.metrics.event('ServiceWorker.waiting');
        }
      } catch (err) {
        this.metrics.event(
          'ServiceWorker.registrationError',
          {level: Level.ERROR},
          err
        );
      }
    }
  }

  /**
   * Prepares Service Worker before user redirects to SUSI in order to improve post login load time.
   * If Service Worker is active already, check manifest to download new version during login.
   * If this is a user's first visit, register the worker.
   */
  public async prepareForLogin(): Promise<Response|undefined> {
    if (navigator.serviceWorker?.controller?.state === 'activated') {
      return await fetch(MANIFEST_EP);
    } else {
      const swCacheExists = window.caches && await caches.has(SW_CACHE_NAME);
      // If the cache already exists, perhaps the user disabled the service worker intentionally
      // Or it was disabled by a feature flag - We won't register in that case.
      if (!swCacheExists) {
        await this.registerServiceWorker();
      }
    }
  }

  /**
   * Sends a fetch request to ping service worker to check the live manifest
   * version and fetch the live HTML to compare versions against the cached versions.
   * Then, update cached assets as needed.
   * @private
   */
  private checkVersions(firstCheck = false): Promise<Response> {
    // Check HTML versions
    const {lastModified} = this.getWindowConfig();
    const htmlPromise = fetch(`/service-worker/indexhtml?lastModified=${lastModified}`);
    // Check manifest versions
    htmlPromise.then(async () => {
      try {
        // Wait 2 minutes before getting manifest to not slow down load.
        const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        await wait(MANIFEST_CHECK_WAIT);
        await runInBackground();
        const res = await fetch(MANIFEST_EP);
        const {data = {}, status} = await res.json();
        if (status === 'outdated') {
          this.metrics.event('ServiceWorker.newManifest', {firstCheck});
        } else if (status === 'not found') {
          this.metrics.event('ServiceWorker.noManifest', {firstCheck});
        } else if (status === 'failed to get manifest') {
          this.metrics.event(
            'ServiceWorker.manifestUpdateFailed',
            Object.assign({firstCheck}, data),
            {level: Level.ERROR}
          );
        } else {
          this.metrics.event('ServiceWorker.noUpdates', {firstCheck});
        }
      } catch (err) {
        this.metrics.event(
          'ServiceWorker.manifestUpdateFailed', {error: err.message, firstCheck}, {level: Level.ERROR}
        );
      }
    });
    return htmlPromise;
  }

  /**
   * Checks the current index.html in the service worker, triggering a version update if needed.
   * @returns {Promise<Response>} check response
   */
  public getHtml(): Promise<Response>|undefined {
    if (navigator.serviceWorker?.controller?.state === 'activated') {
      const htmlPromise = this.checkVersions(true);
      htmlPromise.then(() => {
        this.metrics.event('ServiceWorker.liveHtml');
        setInterval(() => runInBackground().then(() => this.checkVersions()), ONE_HOUR);
      });
      return htmlPromise;
    }
  }

  /**
   * Unregisters the service worker.
   * @param {boolean} disableTemporarily - If true, disables registration for 24 hours
   */
  public async unregister(disableTemporarily = false): Promise<void> {
    ServiceWorkerControllerService.SW_DISABLED = true;
    if (disableTemporarily) {
      const disabled = await storage.local.get('shell_sw_disabled');
      if (!disabled) {
        // Disable for one day.
        await storage.local.set('shell_sw_disabled', 'true', ONE_DAY);
      }
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      registration.unregister().then(success => {
        if (success) {
          this.metrics.event('App.serviceWorkerUnregistered');
        } else {
          this.metrics.event(
            'App.serviceWorkerUnregistrationFail',
            {level: Level.ERROR}
          );
        }
      });
    }
  }
}
